import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Plus, Search, Edit2, Trash2, KeyRound, Cake } from 'lucide-react';
import dayjs from 'dayjs';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Badge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { userService } from '@/services';
import { authService } from '@/services/authService';
import { fmtDate } from '@/utils/format';

export default function Employees() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ search: '', department: '', role: '' });
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', filters, page],
    queryFn: () => userService.list({ ...filters, page, limit: 10 }),
  });

  const remove = (employee) => {
    setConfirmState({
      title: 'Remove employee',
      message: (
        <>
          <b>{employee.name}</b> will be removed from the workspace. Their attendance and work history will remain, but they won't be able to sign in.
        </>
      ),
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          await userService.remove(employee._id);
          toast.success(`${employee.name} removed`);
          qc.invalidateQueries({ queryKey: ['users'] });
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed');
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Employees"
        subtitle="Manage your team — add, edit and reset passwords"
        actions={
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> Add employee
          </button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input
              placeholder="Search by name, email, ID..."
              className="input pl-9 w-72"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <input
            placeholder="Department"
            className="input w-40"
            value={filters.department}
            onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
          />
          <select className="input w-32" value={filters.role} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="employee">Employee</option>
          </select>
        </div>

        {isLoading ? (
          <Loader />
        ) : (data?.items || []).length === 0 ? (
          <EmptyState title="No employees found" />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Employee</th>
                  <th className="th">ID</th>
                  <th className="th">Department</th>
                  <th className="th">Role</th>
                  <th className="th">Leave balance</th>
                  <th className="th">Birthday</th>
                  <th className="th">Joined</th>
                  <th className="th">Status</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u._id}>
                    <td className="td">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold">
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="td">{u.employeeId || '—'}</td>
                    <td className="td">{u.department}</td>
                    <td className="td"><Badge className="bg-slate-100 dark:bg-slate-800 capitalize">{u.role}</Badge></td>
                    <td className="td"><LeaveBalance user={u} /></td>
                    <td className="td">
                      {u.dob ? (
                        dayjs(u.dob).format('MM-DD') === dayjs().format('MM-DD') ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300 font-semibold text-xs">
                            <Cake size={12} /> Today 🎉
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                            <Cake size={13} className="text-pink-400" /> {dayjs(u.dob).format('DD MMM')}
                          </span>
                        )
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="td">{fmtDate(u.joinedAt)}</td>
                    <td className="td">
                      {u.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Active</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">Inactive</Badge>
                      )}
                    </td>
                    <td className="td">
                      <div className="flex gap-1">
                        <button className="btn-ghost p-1" onClick={() => setEditing(u)}><Edit2 size={14} /></button>
                        <button className="btn-ghost p-1" onClick={() => setResetting(u)}><KeyRound size={14} /></button>
                        <button className="btn-ghost p-1 text-rose-600" onClick={() => remove(u)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={10} total={data?.total || 0} onChange={setPage} />
      </Card>

      <EmployeeFormModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['users'] }); }}
      />
      {editing && (
        <EmployeeFormModal
          open
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['users'] }); }}
        />
      )}
      {resetting && (
        <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function EmployeeFormModal({ open, onClose, user, onSaved }) {
  const editing = !!user;
  const defaults = user
    ? {
        ...user,
        // date inputs need a plain YYYY-MM-DD, not the ISO string the API returns
        dob: user.dob ? dayjs(user.dob).format('YYYY-MM-DD') : '',
        // ensure attendance policy fields have sane defaults if missing on legacy users
        workStartTime: user.workStartTime || '09:30',
        lateGraceMinutes: user.lateGraceMinutes ?? 30,
        leaveBalance: user.leaveBalance ?? 0,
        leaveAccrualPerMonth: user.leaveAccrualPerMonth ?? 1,
      }
    : {
        role: 'employee',
        department: 'Engineering',
        workStartTime: '09:30',
        lateGraceMinutes: 30,
        leaveBalance: 0,
        leaveAccrualPerMonth: 1,
      };
  const { register, handleSubmit, formState: { isSubmitting }, reset, watch } = useForm({ defaultValues: defaults });
  // Admins hold no leave balance, so the whole leave block is irrelevant for them.
  // The backend forces these to zero regardless; hiding them keeps the form from
  // offering a setting that will not stick.
  const isAdminRole = watch('role') === 'admin';

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        dob: data.dob || null,
        lateGraceMinutes:
          data.lateGraceMinutes === '' || data.lateGraceMinutes === undefined
            ? 30
            : Number(data.lateGraceMinutes),
        leaveBalance:
          data.leaveBalance === '' || data.leaveBalance === undefined ? 0 : Number(data.leaveBalance),
        leaveAccrualPerMonth:
          data.leaveAccrualPerMonth === '' || data.leaveAccrualPerMonth === undefined
            ? 1
            : Number(data.leaveAccrualPerMonth),
      };
      if (editing) {
        await userService.update(user._id, payload);
        toast.success('Employee updated');
      } else {
        await authService.register(payload);
        toast.success('Employee added');
      }
      reset();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit employee' : 'Add employee'}
      size="lg"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="empForm" className="btn-primary" disabled={isSubmitting}>{editing ? 'Save' : 'Create'}</button>
        </>
      }
    >
      <form id="empForm" onSubmit={handleSubmit(onSubmit)} className="grid sm:grid-cols-2 gap-3">
        <div><label className="label">Name</label><input className="input" {...register('name', { required: true })} /></div>
        <div><label className="label">Email</label><input type="email" className="input" {...register('email', { required: true })} /></div>
        {!editing && (
          <div className="sm:col-span-2">
            <label className="label">Initial password</label>
            <input type="text" className="input" {...register('password', { required: !editing, minLength: 6 })} />
          </div>
        )}
        <div><label className="label">Employee ID</label><input className="input" {...register('employeeId')} /></div>
        <div>
          <label className="label">Role</label>
          <select className="input" {...register('role')}>
            <option value="employee">Employee</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div><label className="label">Department</label><input className="input" {...register('department')} /></div>
        <div><label className="label">Designation</label><input className="input" {...register('designation')} /></div>
        <div><label className="label">Phone</label><input className="input" {...register('phone')} /></div>
        <div>
          <label className="label">Date of birth</label>
          <input type="date" className="input" {...register('dob')} />
          <p className="text-xs text-slate-400 mt-1">Shows on the team calendar & triggers birthday wishes.</p>
        </div>
        {editing && (
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('active')} /> Active
            </label>
          </div>
        )}

        <div className="sm:col-span-2 mt-2 pt-3 border-t border-slate-200 dark:border-slate-800">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Attendance policy
          </div>
        </div>
        <div>
          <label className="label">Office entry time</label>
          <input type="time" className="input" {...register('workStartTime', { required: true })} />
          <p className="text-xs text-slate-400 mt-1">When this employee is expected to check in (default 09:30).</p>
        </div>
        <div>
          <label className="label">Late grace (minutes)</label>
          <input
            type="number"
            min={0}
            max={240}
            className="input"
            {...register('lateGraceMinutes', { valueAsNumber: true })}
          />
          <p className="text-xs text-slate-400 mt-1">Check-ins after entry time + grace are marked late (default 30).</p>
        </div>

        {isAdminRole ? (
          <div className="sm:col-span-2 mt-2 pt-3 border-t border-slate-200 dark:border-slate-800">
            <p className="text-xs text-slate-400">
              Admins are not leave-eligible — no balance, no monthly accrual, and no
              &ldquo;My leaves&rdquo; page.
            </p>
          </div>
        ) : (
          <>
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-slate-200 dark:border-slate-800">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Leave policy
              </div>
            </div>
            <div>
              <label className="label">Leave balance</label>
              <input
                type="number"
                step="0.5"
                className="input"
                {...register('leaveBalance', { valueAsNumber: true })}
              />
              <p className="text-xs text-slate-400 mt-1">Paid leaves available now. Auto-deducts when leave is approved.</p>
            </div>
            <div>
              <label className="label">Monthly accrual</label>
              <input
                type="number"
                min={0}
                step="0.5"
                className="input"
                {...register('leaveAccrualPerMonth', { valueAsNumber: true })}
              />
              <p className="text-xs text-slate-400 mt-1">Credited automatically on the 1st of each month (default 1).</p>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}

// A negative balance is the number that matters at payroll time — it is leave
// taken beyond what was available — so it is called out rather than shown as one
// more grey number in the row.
function LeaveBalance({ user }) {
  if (user.role === 'admin') return <span className="text-slate-400">—</span>;

  const balance = user.leaveBalance ?? 0;
  const perMonth = user.leaveAccrualPerMonth ?? 1;
  const negative = balance < 0;

  return (
    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
      <span className={`font-semibold ${negative ? 'text-rose-600 dark:text-rose-400' : ''}`}>
        {balance}
      </span>
      <span className="text-xs text-slate-400">
        {negative ? `over by ${-balance}` : `+${perMonth}/mo`}
      </span>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();
  const onSubmit = async (data) => {
    try {
      await userService.resetPassword(user._id, data);
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={`Reset password — ${user.name}`}
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="rpForm" className="btn-primary" disabled={isSubmitting}>Reset</button>
        </>
      }
    >
      <form id="rpForm" onSubmit={handleSubmit(onSubmit)}>
        <label className="label">New password</label>
        <input type="text" className="input" {...register('password', { required: true, minLength: 6 })} />
      </form>
    </Modal>
  );
}
